import { Shell, resolveRoute, type QueryClient, type SessionStatus } from "@hakili/app-shell";
import { t } from "@hakili/i18n";
import { ALERTE_PASTILLE, HakiliMark } from "@hakili/ui";
import type { ReactNode } from "react";

import { ONGLETS, ROUTES, TOUS_LES_ECRANS, ongletActif, type EcranClient } from "./routes.js";
import type { ClientStore } from "./store.js";

/**
 * `P0-UIK-016`: the client shell, built for 4.6.7's handset.
 *
 * 4.6.1: *"Single column, `max-w-md` centred, bottom tab bar (`Rechercher`,
 * `Mon dossier`, `Profil`), one screen at a time."* 4.6.7 adds the constraints
 * that make it usable: designed at 360 x 640 first, **no horizontal scroll
 * anywhere, ever**, tap targets at least 44 x 44 px, and the tab bar respecting
 * `env(safe-area-inset-bottom)`.
 *
 * **Danger sentence.** This is the only surface a customer ever sees, and every
 * one of 4.6.7's rules fails invisibly on the machine it was written on. A
 * developer's 1440 px window shows no horizontal scroll, has no safe area to
 * intrude, and hits a 30 px target with a mouse first time. The device that
 * matters is a 360 px Android on Slow 3G in Bamako, and `R1.5` of 08 names the
 * risk in as many words: *"the client journey is built desktop-first and is
 * unusable on a 360 pixel screen"*.
 *
 * **`min-w-0` is load-bearing and looks like noise.** A flex or grid child
 * defaults to `min-width: auto`, which refuses to shrink below its content ,
 * so one long unbroken string, a vehicle reference or an IBAN, pushes the
 * column wider than the viewport and produces exactly the horizontal scroll
 * 4.6.7 forbids. It is the single most common cause of that defect and it never
 * reproduces on a wide screen.
 *
 * **The bottom padding is not decoration either.** The tab bar is fixed, so
 * content behind it would end underneath it , the last row of a result list
 * permanently unreachable, which reads as missing data rather than as a layout
 * fault.
 */

/** 4.6.7: minimum 44 x 44 px tap targets. Named so the spec can assert on it. */
export const CIBLE_TACTILE = "min-h-11 min-w-11";

/** The fixed tab bar's own height, and the padding content needs to clear it. */
export const HAUTEUR_BARRE = "h-16";
export const DEGAGEMENT_BARRE = "pb-20";

export interface ClientShellProps {
  readonly queryClient: QueryClient;
  readonly store: ClientStore;
  readonly status: SessionStatus;
  readonly path: string;
  /**
   * In-app navigation for the tab bar. The tabs stay real `<a href>` elements ,
   * middle-click, long-press and "open in new tab" all still work , and this
   * only intercepts the plain left click, which is the one that would otherwise
   * reload the application and drop the in-memory access token.
   */
  readonly naviguer: (to: string) => void;
  /**
   * `round CW`, `CW.7`: the city the top bar shows, or `null`.
   *
   * The buyer's own, from `/me`, and NEVER a default: a bar that said
   * « Bamako » to a buyer in Ségou would be the product telling him where he
   * lives. Absent, the bar keeps the mark and shows nothing on the right.
   */
  readonly ville: string | null;
  readonly pending: ReactNode;
  readonly signIn: ReactNode;
  /**
   * The screen, RESOLVED. Round 203 made this a render prop, and the defect it
   * closes is worth naming: `children` used to be a fixed node while this
   * component dutifully resolved a route and threw the answer away, so
   * `App.tsx` passed `path="/dossier"` and the client had one screen for ever.
   *
   * A function, so the resolution happens ONCE and here. An app that received
   * the path and resolved it again would be the second copy `views.ts` warns
   * about , *"even a three-character ternary is a second"*.
   */
  readonly children: (ecran: EcranClient, params: Readonly<Record<string, string>>) => ReactNode;
  /**
   * `round CW`, `CW.1`: the rose dot on « Suivi », and it is a FACT.
   *
   * The drawing puts a `rose-500` pastille on that tab when the buyer owes a
   * gesture. It is derived from his own dossier as `GET /me/dossiers/{id}`
   * answers it , its `attente` , and never from a constant: `R81` forbids a
   * mark standing for a count nobody computed, and it was that exact rule that
   * deleted the dealer rail's dot when no notification fact existed.
   *
   * **No dossier, no dot**, which is why this is optional rather than false by
   * default: absent means the shell was told nothing, and the shell draws
   * nothing.
   */
  readonly buyerOwesGesture?: boolean;
}

export function ClientShell({
  queryClient,
  store,
  status,
  path,
  naviguer,
  pending,
  signIn,
  children,
  buyerOwesGesture,
  ville,
}: ClientShellProps): React.JSX.Element {
  const resolution = resolveRoute(ROUTES, path, TOUS_LES_ECRANS);
  const ecran = (
    resolution.kind === "render" ? resolution.route.screen : ROUTES.landingScreen
  ) as EcranClient;
  const params = resolution.kind === "render" ? resolution.params : {};
  const publique = resolution.kind === "render" && resolution.route.public === true;
  const actif = ongletActif(ecran);

  return (
    <Shell
      queryClient={queryClient}
      store={store}
      status={status}
      pending={pending}
      signIn={signIn}
      isPublicRoute={publique}
      crashMessage={t("common.error.inattendue")}
      renderError={(fallback) => (
        <p role="alert" className="p-6 text-sm text-gray-500">
          {fallback.message}
        </p>
      )}
      layout={(outlet) => (
        // `overflow-x-hidden` on the page is the belt to `min-w-0`'s braces:
        // one is the cause fixed, the other the symptom contained. 4.6.7 says
        // "no horizontal scroll anywhere, ever", and "ever" is worth two.
        <div className="min-h-screen overflow-x-hidden bg-gray-50">
          {/**
           * `round CW`, `CW.7`: **the drawing's « Barre superieure », which the
           * build did not have at all.**
           *
           * Found by reading the pairs and by nothing else: every Rechercher
           * state showed the screen starting at its own title, and the drawing
           * opens every authenticated screen with the mark, the name, and the
           * buyer's city on the right.
           *
           * It lives in the SHELL and not in a screen, because the drawing puts
           * it above the whole authenticated app , five screens , and a bar
           * repeated per screen is a bar that comes to differ between two of
           * them.
           *
           * The public deposit route has none, for the reason it has no tab
           * bar: 6.3.6's holder has no session, and chrome that implies one
           * would be inviting him into an app he cannot enter.
           */}
          {publique ? null : (
            <header className="flex shrink-0 items-center gap-2 border-b border-gray-100 bg-white px-5 py-3">
              <HakiliMark />
              <span className="text-base font-bold tracking-tight text-gray-900">hakili</span>
              {ville === null ? null : (
                <span className="ml-auto text-xs text-gray-400" data-testid="client-entete-ville">
                  {ville}
                </span>
              )}
            </header>
          )}

          {/* One column, `max-w-md`, centred , 4.6.1 , and `min-w-0` so a long
              unbroken string shrinks instead of widening the page. */}
          <main className={`mx-auto w-full min-w-0 max-w-md p-4 ${publique ? "" : DEGAGEMENT_BARRE}`}>
            {outlet}
          </main>

          {/* A public deposit route has no tab bar: its holder has no session
              and no other screens, and four tabs he cannot use would only
              invite him to tap them. */}
          {publique ? null : (
            <nav
              aria-label={t("client.nav.dossier")}
              className={`fixed inset-x-0 bottom-0 mx-auto flex w-full max-w-md items-stretch border-t border-gray-200 bg-white pb-[env(safe-area-inset-bottom)] ${HAUTEUR_BARRE}`}
            >
              {ONGLETS.map((onglet) => {
                const Icone = onglet.icon;
                const alerte = onglet.key === "client.nav.suivi" && buyerOwesGesture === true;
                return (
                  <a
                    key={onglet.key}
                    href={onglet.path}
                    onClick={(event) => {
                      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                      event.preventDefault();
                      naviguer(onglet.path);
                    }}
                    aria-current={actif?.key === onglet.key ? "page" : undefined}
                    data-testid={`client-nav-${onglet.path.slice(1)}`}
                    // `CW.5`: `no-underline`, because a tab is not a sentence.
                    // The tab is an `<a>` on purpose , it is a PLACE, and a
                    // middle click should open it , and the browser underlines
                    // links by default, so the first Suivi pair showed four
                    // underlined words the drawing does not underline.
                    className={`flex flex-1 flex-col items-center justify-center gap-1 text-xs no-underline ${CIBLE_TACTILE} ${
                      actif?.key === onglet.key ? "text-indigo-600" : "text-gray-500"
                    }`}
                  >
                    <span className="relative">
                      <Icone className="h-5 w-5" aria-hidden="true" />
                      {alerte ? (
                        <span
                          data-testid="client-nav-suivi-pastille"
                          className={`absolute -right-1 -top-0.5 h-2 w-2 rounded-full ${ALERTE_PASTILLE}`}
                        />
                      ) : null}
                    </span>
                    <span className="font-medium">{t(onglet.key)}</span>
                  </a>
                );
              })}
            </nav>
          )}
        </div>
      )}
    >
      {children(ecran, params)}
    </Shell>
  );
}
